/**
 * Select Component
 *
 * A select dropdown component with search, multi-select, and custom options.
 * Supports keyboard navigation and accessibility features.
 */

import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { cn } from '@utils/cn';

export type SelectSize = 'sm' | 'md' | 'lg';
export type SelectVariant = 'default' | 'filled' | 'outlined';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  description?: string;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'size'> {
  options: SelectOption[];
  size?: SelectSize;
  variant?: SelectVariant;
  placeholder?: string;
  searchable?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  loading?: boolean;
  error?: boolean;
  success?: boolean;
  helperText?: string;
  errorText?: string;
  onChange?: (value: string | string[]) => void;
  onSearch?: (query: string) => void;
  className?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      size = 'md',
      variant = 'default',
      placeholder = 'Select an option...',
      searchable = false,
      multiple = false,
      clearable = false,
      loading = false,
      error = false,
      success = false,
      helperText,
      errorText,
      onChange,
      onSearch,
      className,
      value,
      ...props
    },
    _ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const selectRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const baseClasses = cn('relative', className);

    const triggerClasses = cn(
      'w-full flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-offset-0',
      {
        // Size classes
        'px-3 py-1.5 text-sm': size === 'sm',
        'px-4 py-2 text-base': size === 'md',
        'px-5 py-3 text-lg': size === 'lg',

        // Variant classes
        'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-transparent focus:ring-0 focus:outline-none focus:input-gradient-border':
          variant === 'default' && !error && !success,
        'border-transparent bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:bg-white dark:focus:bg-gray-800 focus:ring-0 focus:outline-none focus:input-gradient-border':
          variant === 'filled' && !error && !success,
        'border-2 border-gray-200 dark:border-gray-700 bg-transparent text-gray-900 dark:text-gray-100 focus:border-transparent focus:ring-0 focus:outline-none focus:input-gradient-border':
          variant === 'outlined' && !error && !success,

        // State classes - only gradient border on focus, no colored borders
        'border-red-200 dark:border-red-800': error,
        'border-green-200 dark:border-green-800': success,
        'opacity-50 cursor-not-allowed': props.disabled,
      }
    );

    const dropdownClasses = cn(
      'absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto'
    );

    const filteredOptions = (options || []).filter(
      option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getDisplayValue = () => {
      if (multiple && Array.isArray(value)) {
        if (value.length === 0) return placeholder;
        if (value.length === 1) {
          const option = (options || []).find(opt => opt.value === value[0]);
          return option?.label || value[0];
        }
        return `${value.length} selected`;
      }

      if (typeof value === 'string') {
        const option = (options || []).find(opt => opt.value === value);
        return option?.label || value;
      }

      return placeholder;
    };

    const handleToggle = () => {
      if (props.disabled || loading) return;
      setIsOpen(!isOpen);
      if (!isOpen && searchable) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    };

    const handleOptionClick = (option: SelectOption) => {
      if (option.disabled) return;

      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = currentValues.includes(option.value)
          ? currentValues.filter(v => v !== option.value)
          : [...currentValues, option.value];
        onChange?.(newValues);
      } else {
        onChange?.(option.value);
        setIsOpen(false);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(multiple ? [] : '');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          handleToggle();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
            handleOptionClick(filteredOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
      }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      setFocusedIndex(-1);
      onSearch?.(query);
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    return (
      <div ref={selectRef} className={baseClasses}>
        <div
          className={triggerClasses}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={props.disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? 'select-options' : undefined}
        >
          <span
            className={cn(
              'flex-1 text-left',
              !value || (Array.isArray(value) && value.length === 0)
                ? 'text-gray-500 dark:text-gray-400'
                : ''
            )}
          >
            {getDisplayValue()}
          </span>

          <div className="flex items-center space-x-2">
            {clearable && value && (Array.isArray(value) ? value.length > 0 : value !== '') && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary-500"></div>
            ) : (
              <svg
                className={cn(
                  'w-4 h-4 text-gray-400 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </div>
        </div>

        {isOpen && (
          <div className={dropdownClasses}>
            {searchable && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search options..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-center px-3 py-2 cursor-pointer transition-colors duration-150',
                      'hover:bg-gray-50 dark:hover:bg-gray-700',
                      focusedIndex === index && 'bg-gray-50 dark:bg-gray-700',
                      option.disabled && 'opacity-50 cursor-not-allowed',
                      multiple &&
                        Array.isArray(value) &&
                        value.includes(option.value) &&
                        'bg-primary-50 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
                    )}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOptionClick(option);
                      }
                    }}
                    role="option"
                    tabIndex={-1}
                    aria-selected={focusedIndex === index}
                  >
                    {option.icon && (
                      <div className="flex-shrink-0 mr-3 text-gray-400">{option.icon}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {option.label}
                      </div>
                      {option.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {option.description}
                        </div>
                      )}
                    </div>
                    {multiple && Array.isArray(value) && value.includes(option.value) && (
                      <div className="flex-shrink-0 ml-2 text-primary-600 dark:text-primary-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Helper text */}
        {(helperText || errorText) && (
          <div className="mt-1 text-sm">
            {error && errorText && (
              <span className="text-error-600 dark:text-error-400">{errorText}</span>
            )}
            {!error && helperText && (
              <span className="text-gray-500 dark:text-gray-400">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
